// Shared Wellness Assessment data — used by both standalone page and in-app modal
// Symptom categories are aligned to Classroom subcategories for content matching

export const PART1_QUESTIONS = [
  {
    id: 'q1',
    question: 'What has changed most for you recently?',
    options: {
      A: 'My body feels different — weight, skin, temperature, or physical discomfort',
      B: 'My mind feels different — foggy thinking, mood swings, or anxiety',
      C: 'My energy feels different — exhausted, poor sleep, or low drive'
    }
  },
  {
    id: 'q2',
    question: 'What bothers you most on a daily basis?',
    options: {
      A: 'Physical symptoms — hot flashes, weight changes, aches, or skin issues',
      B: 'Mental symptoms — difficulty concentrating, emotional ups and downs',
      C: 'Energy symptoms — fatigue, restless sleep, or feeling depleted'
    }
  },
  {
    id: 'q3',
    question: 'Which area would improve your quality of life the most?',
    options: {
      A: 'Feeling comfortable and at home in my body again',
      B: 'Feeling mentally sharp and emotionally balanced',
      C: 'Having steady energy, restful sleep, and feeling vital'
    }
  },
  {
    id: 'q4',
    question: 'If a friend asked what\'s been hardest lately, you\'d say:',
    options: {
      A: 'The physical changes — nothing fits, everything hurts or flushes',
      B: 'The mental load — I can\'t think straight and my emotions are all over',
      C: 'The exhaustion — I\'m running on empty and nothing recharges me'
    }
  },
  {
    id: 'q5',
    question: 'Which statement resonates most?',
    options: {
      A: 'I want to understand what\'s happening to my body and how to manage it',
      B: 'I want my brain back — clarity, calm, and emotional stability',
      C: 'I want to wake up feeling rested and have energy that lasts'
    }
  }
];

// Symptoms organized by pillar, with categories matching Classroom subcategories
// Category names here map directly to Classroom subcategory names for content matching
export const SYMPTOMS = {
  BODY: {
    label: 'Radiant Body',
    categories: {
      'Temperature Changes': [
        'Hot flashes during the day',
        'Night sweats that disrupt sleep',
        'Sudden waves of heat or flushing',
        'Chills or feeling cold after a hot flash',
        'Temperature sensitivity (always too hot or too cold)'
      ],
      'Weight and Nutrition': [
        'Weight gain, especially around midsection',
        'Difficulty losing weight despite efforts',
        'Increased hunger or never feeling satisfied',
        'Sugar or carb cravings',
        'Bloating or digestive changes'
      ],
      'Hair / Skin / Nails': [
        'Dry, dull, or aging skin',
        'Hair thinning or loss',
        'Brittle or breaking nails',
        'Skin breakouts or rashes',
        'Increased wrinkles or loss of elasticity'
      ],
      'Movement and Strength': [
        'Joint aches or stiffness',
        'Muscle weakness or loss of tone',
        'Reduced flexibility',
        'General body aches or inflammation',
        'Headaches or migraines'
      ],
      'Uterus, Bladder and Breasts': [
        'Irregular or changing periods',
        'Heavy or unpredictable bleeding',
        'Breast tenderness or changes',
        'Urinary urgency or leaking',
        'Pelvic pressure or discomfort'
      ]
    }
  },
  MIND: {
    label: 'Balanced Mind',
    categories: {
      'Mental Clarity': [
        'Brain fog or difficulty concentrating',
        'Memory lapses (forgetting words, losing train of thought)',
        'Difficulty making decisions',
        'Feeling mentally slow or sluggish',
        'Trouble multitasking like you used to'
      ],
      'Mood and Anxiety': [
        'Mood swings or emotional sensitivity',
        'Crying easily or feeling overwhelmed by small things',
        'Feeling sad, flat, or losing interest in things you enjoy',
        'Irritability or unexpected anger',
        'Feeling unlike yourself emotionally'
      ],
      'Stress': [
        'Constant worry or racing thoughts',
        'Feeling overwhelmed by normal responsibilities',
        'Physical tension (tight chest, jaw clenching, shallow breathing)',
        'Panic attacks or heart palpitations',
        'Difficulty relaxing or unwinding at the end of the day'
      ]
    }
  },
  VITALITY: {
    label: 'Enduring Vitality',
    categories: {
      'Sleep': [
        'Trouble falling asleep',
        'Waking up during the night (not just from sweats)',
        'Not feeling rested even after a full night',
        'Relying on sleep aids to get through the night',
        'Waking up too early and unable to fall back asleep'
      ],
      'Energy': [
        'Constant fatigue throughout the day',
        'Afternoon energy crashes',
        'No motivation to exercise or move',
        'Needing caffeine or stimulants to function',
        'Feeling exhausted after normal daily activities'
      ],
      'Libido and Pelvis': [
        'Low or absent sex drive',
        'Painful intercourse',
        'Vaginal dryness or discomfort',
        'Loss of sensation or pleasure',
        'Feeling disconnected from your body physically'
      ]
    }
  }
};

export const PILLAR_DETAILS = {
  BODY: {
    label: 'Body',
    fullLabel: 'Radiant Body',
    color: 'bg-dark-teal',
    tagColor: 'bg-dark-teal hover:bg-dark-teal/80',
    description: 'Your body is signaling that it needs attention. Physical changes during perimenopause and menopause — from temperature swings to weight shifts to skin changes — respond powerfully to the right support.',
    recommendations: [
      'Anti-inflammatory nutrition strategies',
      'Targeted supplementation for hormonal support',
      'Movement protocols designed for this stage',
      'Understanding your changing metabolism'
    ]
  },
  MIND: {
    label: 'Mind',
    fullLabel: 'Balanced Mind',
    color: 'bg-[#2563EB]',
    tagColor: 'bg-[#002447] hover:bg-[#002447]/80',
    description: 'Your mind is ready for support. Cognitive changes, mood shifts, and anxiety are among the most common — yet most underrecognized — symptoms of hormonal transition. There are real, evidence-based strategies that help.',
    recommendations: [
      'Cognitive support through targeted nutrients',
      'Stress resilience and nervous system regulation',
      'Mood-stabilizing lifestyle adjustments',
      'Mindfulness practices designed for this stage'
    ]
  },
  VITALITY: {
    label: 'Vitality',
    fullLabel: 'Enduring Vitality',
    color: 'bg-[#D4AF37]',
    tagColor: 'bg-[#D4AF37] hover:bg-[#D4AF37]/80',
    description: 'Your vitality is calling for restoration. Energy, sleep, and libido are deeply connected to hormonal balance — and strategic changes can make a dramatic difference in how you feel every single day.',
    recommendations: [
      'Sleep optimization strategies',
      'Energy-boosting nutrition and meal timing',
      'Hormonal support for libido and vitality',
      'Recovery and adrenal health protocols'
    ]
  }
};

// Map symptom categories to Classroom subcategory names for content linking
export const CATEGORY_TO_SUBCATEGORY = {
  'Temperature Changes': { pillar: 'radiant_body', subcategory: 'Temperature Changes' },
  'Weight and Nutrition': { pillar: 'radiant_body', subcategory: 'Weight and Nutrition' },
  'Hair / Skin / Nails': { pillar: 'radiant_body', subcategory: 'Hair / Skin / Nails' },
  'Movement and Strength': { pillar: 'radiant_body', subcategory: 'Movement and Strength' },
  'Uterus, Bladder and Breasts': { pillar: 'radiant_body', subcategory: 'Uterus, Bladder and Breasts' },
  'Mental Clarity': { pillar: 'balanced_mind', subcategory: 'Mental Clarity' },
  'Mood and Anxiety': { pillar: 'balanced_mind', subcategory: 'Mood and Anxiety' },
  'Stress': { pillar: 'balanced_mind', subcategory: 'Stress' },
  'Sleep': { pillar: 'enduring_vitality', subcategory: 'Sleep' },
  'Energy': { pillar: 'enduring_vitality', subcategory: 'Energy' },
  'Libido and Pelvis': { pillar: 'enduring_vitality', subcategory: 'Libido and Pelvis' }
};

// Generate a Classroom URL for a given symptom category
export const getCategoryClassroomUrl = (categoryName) => {
  const mapping = CATEGORY_TO_SUBCATEGORY[categoryName];
  if (!mapping) return '/classroom';
  const subcategorySlug = mapping.subcategory.toLowerCase().replace(/[\s/]+/g, '-');
  return `/classroom/${mapping.pillar}/${subcategorySlug}`;
};
